import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, ENVIRONMENT_INITIALIZER, inject, makeEnvironmentProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { measure, logMeasurement, getInstrument, createConnection } from '@getpolaris.ai/sdk';
import { catchError } from 'rxjs';
import { tap } from 'rxjs/operators';

const POLARIS_OPTIONS = new InjectionToken('POLARIS_OPTIONS');

class PolarisInterceptor {
    constructor(options) {
        this.options = options;
    }
    intercept(req, next) {
        if (!this.options.interceptor?.enabled) {
            return next.handle(req);
        }
        const instrument = measure(this.options.interceptor?.eventName ?? 'request', {
            method: req.method,
            url: req.url,
        });
        return next.handle(req).pipe(tap((event) => {
            if (event.type === HttpEventType.Response) {
                instrument.done({
                    status: event.status,
                });
            }
        }), catchError((error) => {
            instrument.fail({ error });
            throw error;
        }));
    }
}
PolarisInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: PolarisInterceptor, deps: [{ token: POLARIS_OPTIONS }], target: i0.ɵɵFactoryTarget.Injectable });
PolarisInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: PolarisInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: PolarisInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [POLARIS_OPTIONS]
                }] }]; } });

class PolarisService {
    constructor(options) {
        this.options = options;
        this.measure = measure;
        this.logMeasurement = logMeasurement;
        this.getInstrument = getInstrument;
        this.connect();
    }
    connect() {
        const { apiKey, apiUrl } = this.options;
        createConnection({ apiKey, apiUrl }).catch(console.error);
    }
}
PolarisService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: PolarisService, deps: [{ token: POLARIS_OPTIONS }], target: i0.ɵɵFactoryTarget.Injectable });
PolarisService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: PolarisService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: PolarisService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [POLARIS_OPTIONS]
                }] }]; } });

function _providePolaris(options) {
    return [
        {
            provide: POLARIS_OPTIONS,
            useValue: options,
        },
        PolarisInterceptor,
        PolarisService,
        {
            provide: ENVIRONMENT_INITIALIZER,
            useFactory: () => {
                return () => {
                    inject(PolarisService);
                    inject(PolarisInterceptor);
                };
            },
            multi: true,
        },
    ];
}
/**
 * Connects Polaris to your Angular application.
 *
 * ```ts
 * bootstrapApplication(AppComponent, {
 *   providers: [
 *     providePolaris({ options }),
 *   ],
 * });
 * ```
 */
function providePolaris(options) {
    return makeEnvironmentProviders(_providePolaris(options));
}

/**
 * Connects Polaris to your Angular application.
 *
 * ```ts
 * @NgModule({
 *  imports: [
 *    PolarisModule.forRoot({ options }),
 *  ],
 *  declarations: [AppComponent],
 *  bootstrap: [AppComponent],
 *  })
 *  export class AppModule {}
 * ```
 */
class PolarisModule {
    static forRoot(options) {
        return {
            ngModule: PolarisModule,
            providers: _providePolaris(options),
        };
    }
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('PolarisModule is already loaded. Import it in the AppModule only');
        }
    }
}
PolarisModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: PolarisModule, deps: [{ token: PolarisModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule });
PolarisModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.9", ngImport: i0, type: PolarisModule });
PolarisModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: PolarisModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: PolarisModule, decorators: [{
            type: NgModule,
            args: [{}]
        }], ctorParameters: function () { return [{ type: PolarisModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { PolarisModule, PolarisService, providePolaris };
